import { createFileRoute, Link } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { CtaLink } from "@/components/marketing/CtaLink";
import { ArrowRight, Sparkles, Globe, Heart, Shield, Eye, Zap } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import heroAsset from "@/assets/about/hero.webp.asset.json";
import storyAsset from "@/assets/about/story.webp.asset.json";

const heroImg = heroAsset.url;
const storyImg = storyAsset.url;
const heroAlt =
  "Smiling Banana customer in a tailored blazer holding a purple NFC business card in a modern high-rise office overlooking the city skyline";
const storyAlt =
  "Side-by-side comparison of a worn paper Six Studio business card and the same contact reborn as a Banana digital profile on iPhone with a purple NFC smart card";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — banana.cards" },
      {
        name: "description",
        content:
          "Banana is the AI-powered contact platform helping 200,000+ professionals turn conversations into lasting relationships. Meet our team and story.",
      },
      { property: "og:title", content: "About banana.cards" },
      {
        property: "og:description",
        content: "Our story, our mission and the people behind banana.cards.",
      },
      { property: "og:image", content: heroImg },
      { property: "og:locale", content: "en_US" },
    ],
    links: alternateLinkList("/about", "en"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About banana.cards",
          description: "Banana is the AI-powered contact platform helping 200,000+ professionals turn conversations into lasting relationships.",
          url: "https://banana.cards/about",
          mainEntity: {
            "@type": "Organization",
            name: "Banana Cards",
            url: "https://banana.cards",
            foundingDate: "2022",
            parentOrganization: { "@type": "Organization", name: "SIX STUDIO CREATIVE - FZCO" },
          },
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 md:grid-cols-2 md:px-6">
          <div className="flex flex-col justify-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">About</p>
            <h1 className="mt-3 font-display text-3xl leading-[1.1] md:text-5xl">
              We help people <em className="italic">remember people.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
              Banana started with a simple idea: networking shouldn't end at "nice to meet you". We're building
              the AI-powered platform that turns conversations into lasting relationships — and relationships into
              real-world growth, deals and friendships.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink
                to="/signup"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]"
              >
                Try Banana free <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/70"
              >
                Talk to the team
              </Link>
            </div>
          </div>
          <img src={heroImg} alt={heroAlt} className="w-full rounded-2xl object-cover" />
        </div>
      </section>

      {/* STATS */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="grid gap-4 md:grid-cols-4">
            {[
              { v: "2022", l: "Founded" },
              { v: "30+", l: "Team across 9 countries" },
              { v: "200k+", l: "Professionals on Banana" },
              { v: "12M+", l: "Connections made" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-card p-8">
                <p className="font-display text-5xl leading-none">{s.v}</p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 md:grid-cols-2 md:px-6">
          <img src={storyImg} alt={storyAlt} className="w-full rounded-2xl object-cover" />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Our story</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              From a paper card in a drawer, to <em className="italic">a brain in your pocket.</em>
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                Banana was founded in 2022 after our team kept losing track of the people we met at conferences,
                investor meetings and customer calls. Paper cards ended up in a drawer. LinkedIn was a graveyard of
                people we couldn't place. Our CRMs were empty.
              </p>
              <p>
                We started with one product — a beautifully designed NFC card that opened a digital profile in
                one tap. People loved it. But the real magic happened when we added AI: meeting notes that wrote
                themselves, follow-ups drafted in your voice, and reminders that knew when a contact actually
                mattered again.
              </p>
              <p>
                Today, Banana is used by 200,000+ professionals — from solo founders to enterprise sales orgs —
                in 80+ countries. We're still a small team, still bootstrapped, and still obsessed with the same
                question: how do we help humans be better at being human?
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-muted">
        <div className="mx-auto max-w-5xl px-4 py-24 md:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Timeline</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Four years, <em className="italic">a lot of cards.</em>
          </h2>
          <ol className="mt-10 grid gap-4 md:grid-cols-2">
            {[
              { y: "2022", t: "Founded", d: "Three co-founders, one prototype card, and a Google Sheet of beta users." },
              { y: "2023", t: "First 10,000 customers", d: "Launched in metal, bamboo and gold. AI notetaker shipped in Q4." },
              { y: "2024", t: "The CRM era", d: "Full pipelines, contracts, bookings and automations — one platform, one price." },
              { y: "2025", t: "Enterprise & global", d: "SOC 2 Type II, SAML SSO, data residency in EU/US/APAC. 200k+ users." },
            ].map((m) => (
              <li key={m.y} className="rounded-2xl bg-card p-6">
                <p className="font-display text-3xl">{m.y}</p>
                <h3 className="mt-2 text-lg font-bold">{m.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-24 md:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Principles</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            What we believe — <em className="italic">written down, on purpose.</em>
          </h2>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {[
              { i: Heart, t: "People first, always.", d: "Software is only useful when it makes humans better at being human. We optimize for the conversation, not the click." },
              { i: Sparkles, t: "AI augments, never replaces.", d: "We use AI to remove busywork, not to fake warmth or shortcut relationships. You always sound like you." },
              { i: Shield, t: "Privacy by default.", d: "Your contacts are yours. We never train models on your data, and we sell to no one. Export anything, anytime." },
              { i: Eye, t: "Beautiful is the baseline.", d: "Tools you use daily should be a joy to look at. Every pixel, every card, every email — designed with intent." },
              { i: Zap, t: "Speed is a feature.", d: "We obsess over latency, animation and the time-to-first-tap. The fastest tool wins your habits." },
              { i: Globe, t: "Built for the whole world.", d: "Cards ship to 80+ countries. AI speaks 50+ languages. Data lives where your laws say it should." },
            ].map((p) => {
              const Icon = p.i;
              return (
                <div key={p.t} className="rounded-2xl bg-muted p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-2xl">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">The team</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            30 people, <em className="italic">9 timezones, one obsession.</em>
          </h2>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">
            We're a fully remote team of designers, engineers, AI researchers and operators who've shipped software
            at Apple, Stripe, Notion, Loom and Airbnb. We meet in person quarterly — usually somewhere with good food.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: "Engineering", c: "12 people" },
              { n: "Design", c: "5 people" },
              { n: "AI & Research", c: "4 people" },
              { n: "Go-to-market", c: "9 people" },
            ].map((d) => (
              <div key={d.n} className="rounded-2xl bg-card p-6">
                <div className="aspect-square w-full rounded-xl bg-muted" aria-hidden />
                <h3 className="mt-4 font-bold">{d.n}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{d.c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTORS / PRESS */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-24 md:px-6">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Backed by</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1]">Operators who've been here.</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Bootstrapped through 2023, then partnered with operators and angels from Apple, Stripe, Notion,
                Loom and Airbnb. We're capital-efficient, profitable, and building for the long run.
              </p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">In the press</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1]">A small dent so far.</h2>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li>— "The most beautiful business card I've ever owned." <span className="text-foreground">Wired</span></li>
                <li>— "Banana's CRM is what Salesforce should've been." <span className="text-foreground">The Verge</span></li>
                <li>— "Networking, finally re-invented." <span className="text-foreground">Fast Company</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_50%)]" aria-hidden />
            <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              Want to build the future of relationships <em className="italic text-brand-lime">with us?</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              We're always looking for designers, engineers and operators who care about craft. Or just say hello.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/teams" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
                See open roles
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
                Get in touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
